// Card details panel (feature catalogue C9 link, C10 description): title, status, colour, owner,
// due date, link and a rich text description. Opened from the card menu, by double-clicking the
// card header or with the key I. Every field saves on its own (one undo step each); the
// description saves after a short pause and merges with what others write at the same time
// (rich.ts, lanewerk-core board/rich.rs). "Verlauf" lists who changed what on the card (C11,
// written by the server: GET /api/boards/{id}/cards/{card}/history).
import type { Status } from "./engine-api";
import { formatPoints } from "./iso";
import { STATUS, STICKY, css, personColor } from "./palette";
import type { RichNotes } from "./rich";
import type { Store } from "./store";

/** The description editor (Quill) is loaded on the first opened card, not with the board. */
let richModule: Promise<typeof import("./rich")> | null = null;
const loadRich = () => (richModule ??= import("./rich"));

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string) => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
};
/** The history is read again this long after a change (the server writes it when the change arrives). */
const HISTORY_AFTER = 1200;

export interface HistoryEntry { at: number; by: string | null; card: string; what: string[] }
const WHAT: Record<string, string> = {
  created: "angelegt", deleted: "gelöscht", title: "Titel", status: "Status", color: "Farbe", owner: "Verantwortlich",
  due: "Fälligkeit", moved: "verschoben", size: "Grösse", link: "Link", points: "Story Points", notes: "Beschreibung", assignees: "Mitwirkende",
};

/** Entries of the same person within five minutes become one line (newest time, all fields). */
export function groupHistory(list: HistoryEntry[]): HistoryEntry[] {
  const out: HistoryEntry[] = [];
  for (const e of list) {
    const last = out[out.length - 1];
    if (last && last.by === e.by && last.at - e.at < 5 * 60_000 && !e.what.includes("created") && !last.what.includes("deleted")) {
      last.what = [...new Set([...last.what, ...e.what])];
    } else out.push({ ...e, what: [...e.what] });
  }
  return out;
}

const timeFmt = new Intl.DateTimeFormat("de-CH", { hour: "2-digit", minute: "2-digit" });
const dayFmt = new Intl.DateTimeFormat("de-CH", { day: "2-digit", month: "2-digit" });
/** "gerade eben", "vor 12 Min.", "14:32", "gestern 14:32", "17.10. 14:32". */
export function when(ms: number, now = Date.now()): string {
  const d = new Date(ms), n = new Date(now);
  const mins = Math.round((now - ms) / 60_000);
  if (mins < 1) return "gerade eben";
  if (mins < 60) return `vor ${mins} Min.`;
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((day(n) - day(d)) / 864e5);
  if (days === 0) return timeFmt.format(d);
  if (days === 1) return `gestern ${timeFmt.format(d)}`;
  return `${dayFmt.format(d)} ${timeFmt.format(d)}`;
}

export class DetailsPanel {
  private panel = document.getElementById("lw-details") as HTMLElement;
  private id: string | null = null;
  private rich: RichNotes | null = null;
  private readOnly = false;
  private historyTimer: ReturnType<typeof setTimeout> | null = null;
  private historyAll = false;
  /** Only the newest history request may fill the list (replies can come back out of order). */
  private historySeq = 0;

  constructor(private store: Store, private onClose: () => void, private boardId = "") {
    this.panel.addEventListener("keydown", (e) => {
      e.stopPropagation();
      if (e.key === "Escape" && !e.isComposing) { e.preventDefault(); this.close(); }
    });
    store.subscribe((source) => {
      if (!this.id || source === "view") return;
      if (!store.card(this.id)) return this.close();
      if (source === "remote") this.refresh();
      this.rich?.sync(); // others' edits, but also ⌘Z on the board or a refused change of ours
      this.scheduleHistory();
    });
  }

  get cardId() { return this.id; }

  setReadOnly(ro: boolean) {
    if (ro === this.readOnly) return; // every reconnect reports the role: keep the editor as it is
    this.readOnly = ro;
    if (this.id) this.render(false);
  }

  open(id: string) {
    if (this.id !== id) this.flush();
    this.id = id;
    this.panel.hidden = false;
    this.render(true);
  }

  close() {
    if (!this.id) return;
    this.flush();
    this.rich?.destroy();
    this.rich = null;
    this.id = null;
    this.panel.hidden = true;
    this.onClose();
  }

  /** Writes a pending description edit now (closing, switching cards). */
  flush() {
    this.rich?.flush();
  }

  /** Someone else changed the card: update the fields that are not being edited. */
  private refresh() {
    const id = this.id!;
    const c = this.store.card(id);
    if (!c) return;
    const active = document.activeElement;
    const title = this.panel.querySelector<HTMLTextAreaElement>("#lw-d-title");
    if (title && active !== title) title.value = c.title;
    const owner = this.panel.querySelector<HTMLInputElement>("#lw-d-owner");
    if (owner && active !== owner) owner.value = c.owner ?? "";
    const due = this.panel.querySelector<HTMLInputElement>("#lw-d-due");
    if (due && active !== due) due.value = c.due ?? "";
    const points = this.panel.querySelector<HTMLInputElement>("#lw-d-points");
    if (points && active !== points) { points.value = c.points == null ? "" : formatPoints(c.points); points.removeAttribute("aria-invalid"); }
    const link = this.panel.querySelector<HTMLInputElement>("#lw-d-link");
    if (link && active !== link) { link.value = c.link ?? ""; this.updateOpen(c.link); }
    for (const b of this.panel.querySelectorAll<HTMLButtonElement>("[data-status]")) b.setAttribute("aria-pressed", String(Number(b.dataset.status) === c.status));
    for (const b of this.panel.querySelectorAll<HTMLButtonElement>("[data-color]")) b.setAttribute("aria-pressed", String(Number(b.dataset.color) === c.color));
    const week = this.panel.querySelector<HTMLElement>("#lw-d-week");
    if (week) week.textContent = c.week ? `Liegt in ${c.week.replace("-W", " · KW ")}` : "";
    this.renderPeople();
    this.panel.style.setProperty("--card", css((STICKY[c.color] ?? STICKY[0]).rgb));
  }

  private updateOpen(link: string | null | undefined) {
    const a = this.panel.querySelector<HTMLAnchorElement>("#lw-d-open");
    if (!a) return;
    a.hidden = !link;
    if (link) a.href = link;
  }

  /** Builds the form; `focus`: the description when there is one, else the title. */
  private render(focus: boolean) {
    const id = this.id!;
    const c = this.store.card(id)!;
    const ro = this.readOnly;
    this.panel.style.setProperty("--card", css((STICKY[c.color] ?? STICKY[0]).rgb));
    const form = el("div", "fe-form details");
    const head = el("div", "share-head");
    head.appendChild(el("h2", "fe-title", "Karte"));
    const x = el("button", "fe-icon share-close", "×");
    x.type = "button";
    x.setAttribute("aria-label", "Schliessen");
    x.addEventListener("click", () => this.close());
    head.appendChild(x);
    form.appendChild(head);

    const field = (label: string, input: HTMLElement, forId: string) => {
      const f = el("div", "fe-field");
      const l = el("label", "menu-title", label);
      l.htmlFor = forId;
      f.append(l, input);
      form.appendChild(f);
      return f;
    };

    const title = el("textarea", "field d-title");
    title.id = "lw-d-title";
    title.rows = 2;
    title.maxLength = 500;
    title.value = c.title;
    title.disabled = ro;
    const saveTitle = () => {
      const t = title.value.replace(/\s+/g, " ").trim();
      if (this.id === id && t !== this.store.card(id)?.title) this.store.setTitle(id, t);
    };
    title.addEventListener("keydown", (e) => { if (e.key === "Enter") { e.preventDefault(); saveTitle(); title.blur(); } });
    title.addEventListener("blur", saveTitle);
    field("Titel", title, "lw-d-title");

    const status = el("div", "menu-row");
    status.setAttribute("role", "group");
    status.setAttribute("aria-label", "Status");
    ([1, 2, 4, 3] as Status[]).forEach((s) => {
      const b = el("button", "chip", STATUS[s].label);
      b.type = "button";
      b.dataset.status = String(s);
      b.disabled = ro;
      b.style.setProperty("--dot", css(STATUS[s].rgb));
      b.setAttribute("aria-pressed", String(c.status === s));
      b.addEventListener("click", () => this.store.setStatus([id], s));
      status.appendChild(b);
    });
    const st = el("div", "fe-field");
    st.append(el("span", "menu-title", "Status"), status);
    form.appendChild(st);

    const colors = el("div", "swatches");
    colors.setAttribute("role", "group");
    colors.setAttribute("aria-label", "Farbe");
    STICKY.forEach((s, i) => {
      const b = el("button", "swatch");
      b.type = "button";
      b.dataset.color = String(i);
      b.disabled = ro;
      b.title = s.name;
      b.setAttribute("aria-label", s.name);
      b.style.background = css(s.rgb);
      b.setAttribute("aria-pressed", String(c.color === i));
      b.addEventListener("click", () => this.store.setColor([id], i));
      colors.appendChild(b);
    });
    const co = el("div", "fe-field");
    co.append(el("span", "menu-title", "Farbe"), colors);
    form.appendChild(co);

    const row = el("div", "d-row");
    const owner = el("input", "field");
    owner.id = "lw-d-owner";
    owner.maxLength = 4;
    owner.placeholder = "z. B. JUEN";
    owner.value = c.owner ?? "";
    owner.disabled = ro;
    owner.addEventListener("change", () => this.store.setOwner([id], owner.value.trim().toUpperCase()));
    const due = el("input", "field");
    due.id = "lw-d-due";
    due.type = "date";
    due.value = c.due ?? "";
    due.disabled = ro;
    due.addEventListener("change", () => this.store.setDue([id], due.value));
    const points = el("input", "field");
    points.id = "lw-d-points";
    points.inputMode = "decimal";
    points.maxLength = 6;
    points.placeholder = "–";
    points.value = c.points == null ? "" : formatPoints(c.points);
    points.disabled = ro;
    points.addEventListener("change", () => {
      const ok = this.store.setPoints([id], points.value);
      points.setAttribute("aria-invalid", String(!ok));
      if (!ok) points.title = "Eine Zahl von 0 bis 999";
      else points.removeAttribute("title");
    });
    const o = el("div", "fe-field");
    const ol = el("label", "menu-title", "Verantwortlich");
    ol.htmlFor = "lw-d-owner";
    o.append(ol, owner);
    const d = el("div", "fe-field");
    const dl = el("label", "menu-title", "Fällig am");
    dl.htmlFor = "lw-d-due";
    d.append(dl, due);
    const sp = el("div", "fe-field");
    const spl = el("label", "menu-title", "Punkte");
    spl.htmlFor = "lw-d-points";
    spl.title = "Story Points";
    sp.append(spl, points);
    row.append(o, d, sp);
    form.appendChild(row);
    const week = el("div", "menu-hint", c.week ? `Liegt in ${c.week.replace("-W", " · KW ")}` : "");
    week.id = "lw-d-week";
    form.appendChild(week);

    // assignees ("Mitwirkende"): chips to remove, a field to add (Enter or comma)
    const pf = el("div", "fe-field");
    const pl = el("label", "menu-title", "Mitwirkende");
    pl.htmlFor = "lw-d-person";
    const chips = el("div", "d-people");
    chips.id = "lw-d-people";
    const add = el("input", "field");
    add.id = "lw-d-person";
    add.maxLength = 4;
    add.placeholder = ro ? "" : "Kürzel + Enter";
    add.disabled = ro;
    add.hidden = ro;
    const addPerson = () => {
      const v = add.value.trim().toUpperCase();
      if (!v || this.id !== id) return;
      const now = this.store.card(id)?.assignees ?? [];
      if (!now.includes(v)) this.store.setAssignees(id, [...now, v]);
      add.value = "";
      this.renderPeople();
    };
    add.addEventListener("keydown", (e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addPerson(); } });
    add.addEventListener("blur", addPerson);
    pf.append(pl, chips, add);
    form.appendChild(pf);

    const linkRow = el("div", "d-link");
    const link = el("input", "field");
    link.id = "lw-d-link";
    link.type = "url";
    link.inputMode = "url";
    link.placeholder = "https://…";
    link.value = c.link ?? "";
    link.disabled = ro;
    const open = el("a", "d-open", "Öffnen");
    open.id = "lw-d-open";
    open.target = "_blank";
    open.rel = "noopener noreferrer";
    open.hidden = !c.link;
    if (c.link) open.href = c.link;
    const err = el("p", "fe-error");
    err.setAttribute("role", "alert");
    const saveLink = () => {
      if (this.id !== id) return;
      const v = link.value.trim();
      if (v === (this.store.card(id)?.link ?? "")) { err.textContent = ""; return; }
      if (this.store.setLink(id, v)) {
        err.textContent = "";
        const l = this.store.card(id)?.link ?? "";
        link.value = l;
        this.updateOpen(l);
      } else {
        err.textContent = "Nur Web-Adressen (https://…) und E-Mail-Links (mailto:…).";
      }
    };
    link.addEventListener("change", saveLink);
    link.addEventListener("keydown", (e) => { if (e.key === "Enter") { e.preventDefault(); saveLink(); } });
    linkRow.append(link, open);
    const lf = field("Link", linkRow, "lw-d-link");
    lf.appendChild(err);

    this.rich?.destroy();
    this.rich = null;
    const nf = el("div", "fe-field");
    const nl = el("span", "menu-title", "Beschreibung");
    nl.id = "lw-d-notes-label";
    const slot = el("div", "rt-slot");
    nf.append(nl, slot);
    form.appendChild(nf);
    const hasNotes = this.store.card(id)?.notes ?? false;
    void loadRich().then(({ RichNotes }) => {
      if (this.id !== id || !slot.isConnected) return; // closed or re-rendered meanwhile
      const rich = new RichNotes(this.store, this.boardId, id, ro);
      rich.editor.setAttribute("aria-labelledby", nl.id);
      rich.editor.removeAttribute("aria-label");
      slot.replaceWith(rich.root);
      this.rich = rich;
      if (focus && hasNotes && !this.panel.contains(document.activeElement)) {
        rich.focus(); // Quill focuses without scrolling: bring the field into view
        rich.root.scrollIntoView({ block: "nearest" });
      }
    }).catch(() => { slot.textContent = "Der Editor konnte nicht geladen werden – Seite neu laden."; });

    const hist = el("section", "d-history");
    hist.id = "lw-d-history";
    hist.hidden = true;
    hist.append(el("span", "menu-title", "Verlauf"), el("ol", "d-history-list"));
    form.appendChild(hist);
    this.historyAll = false;
    void this.loadHistory();

    this.panel.replaceChildren(form);
    this.renderPeople();
    if (focus && !hasNotes) title.focus();
  }

  private scheduleHistory() {
    if (this.historyTimer) clearTimeout(this.historyTimer);
    this.historyTimer = setTimeout(() => { this.historyTimer = null; void this.loadHistory(); }, HISTORY_AFTER);
  }

  /** Who changed what (shown when the server keeps a history for this board). */
  private async loadHistory() {
    const id = this.id;
    if (!id || !this.boardId) return;
    const seq = ++this.historySeq;
    let list: HistoryEntry[];
    try {
      const r = await fetch(`./api/boards/${encodeURIComponent(this.boardId)}/cards/${encodeURIComponent(id)}/history`, { headers: { accept: "application/json" } });
      if (!r.ok) return;
      list = (await r.json()) as HistoryEntry[];
    } catch { return; }
    const box = this.panel.querySelector<HTMLElement>("#lw-d-history");
    if (!box || this.id !== id || seq !== this.historySeq) return;
    const lines = groupHistory(list);
    box.hidden = lines.length === 0;
    const shown = this.historyAll ? lines : lines.slice(0, 6);
    const ol = box.querySelector("ol")!;
    ol.replaceChildren(...shown.map((e) => {
      const li = el("li", "d-history-item");
      const t = el("time", "d-history-when", when(e.at));
      t.dateTime = new Date(e.at).toISOString();
      t.title = new Date(e.at).toLocaleString("de-CH");
      li.append(t, el("span", "d-history-who", e.by ?? "Jemand"), el("span", "d-history-what", e.what.map((w) => WHAT[w] ?? w).join(", ")));
      return li;
    }));
    box.querySelector(".d-history-more")?.remove();
    if (lines.length > shown.length) {
      const more = el("button", "menu-action d-history-more", `Alle ${lines.length} Änderungen`);
      more.type = "button";
      more.addEventListener("click", () => { this.historyAll = true; void this.loadHistory(); });
      box.appendChild(more);
    }
  }

  private renderPeople() {
    const box = this.panel.querySelector<HTMLElement>("#lw-d-people");
    const id = this.id;
    if (!box || !id) return;
    const people = this.store.card(id)?.assignees ?? [];
    box.replaceChildren(...people.map((p) => {
      const chip = el("span", "d-person");
      const dot = el("span", "d-dot");
      dot.style.background = css(personColor(p));
      chip.append(dot, el("span", undefined, p));
      if (!this.readOnly) {
        const x = el("button", "d-person-x", "×");
        x.type = "button";
        x.setAttribute("aria-label", `${p} entfernen`);
        x.addEventListener("click", () => {
          this.store.setAssignees(id, (this.store.card(id)?.assignees ?? []).filter((q) => q !== p));
          this.renderPeople();
        });
        chip.appendChild(x);
      }
      return chip;
    }));
    if (!people.length) box.appendChild(el("span", "menu-hint", this.readOnly ? "Niemand" : "Noch niemand – Kürzel eingeben"));
  }
}
